import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

dayjs.extend(utc);
dayjs.extend(timezone);

// Format in a fixed zone so pre_render (UTC) and the client (SGT) agree.
const DISPLAY_TIMEZONE = "Asia/Singapore";

type UseDateFormatProps = {
  date: Date;
  shouldShowDayOfWeek?: boolean;
};

/**
 * Custom hook to format a date into a localized string.
 *
 * @param {Date} props.date - The date to be formatted.
 * @param {boolean} [props.shouldShowDayOfWeek=false] - Flag to include the day of the week in the formatted string.
 * @returns {string} The formatted date string.
 */
export default function useDateFormat({
  date,
  shouldShowDayOfWeek = false,
}: UseDateFormatProps): string {
  const { t, i18n } = useTranslation();

  const formattedString = useMemo(() => {
    const zonedDate = dayjs(date).tz(DISPLAY_TIMEZONE);
    const month = zonedDate.format("MMMM");
    const day = zonedDate.format("D");
    const year = zonedDate.format("YYYY");
    const translateMonth = t(`home:months.${month}`);
    const dayOfWeek = t(`home:days.${zonedDate.format("dddd")}`);

    if (shouldShowDayOfWeek) {
      return dayOfWeek;
    } else {
      return t("common:dateFormat", {
        month: translateMonth,
        date: day,
        year,
      });
    }
  }, [date, shouldShowDayOfWeek, t, i18n.language]);

  return formattedString;
}
